// src/theme/designTokens.js
/**
 * Professional Design System
 * Single source of truth for all UI/UX styling
 * Ensures consistency across the entire application
 */

/**
 * UI SCALE — the one number that sets how large the whole interface renders.
 *
 * Change this and every page resizes together: landing, login, register,
 * reset-password, dashboard, new application, track, reports, analytics and
 * payment. Nothing else has to be touched — no per-page constant, no
 * per-component fontSize, no stylesheet. It is applied exactly once, as a CSS
 * `zoom` on the #app-a11y-root wrapper App.js puts around the entire routed
 * app, so every px in the tree (type, padding, icons, borders) scales in step
 * and the layout stays internally proportional.
 *
 * 100 = the sizes as authored. Lower renders tighter — 92 is what a 92% browser
 * zoom used to give, which is where this product's chrome was really designed
 * to sit on the 1280px-class effective viewport most users have (150% Windows
 * scaling on a 1920 panel).
 *
 * Applied by src/components/a11y/AccessibilityWidget.js, which also lets a user
 * step their own size up or down from this baseline. Moving this number
 * re-baselines everyone who never made such a choice; anyone who did keeps it.
 */
export const uiScale = 92;   // percent

export const colors = {
  // Primary Suite - Core brand colors
  primary: {
    navy: '#0B2458',      // Primary text, emphasis, headers
    blue: '#1558C0',      // Primary actions, links
    lightBlue: '#3B82F6', // Secondary actions, hover states
    dark: '#0A1F3C',      // Very dark navy for high contrast
  },

  // Accent Suite - Semantic colors
  accent: {
    teal: '#0D9488',      // Success, validation, positive
    amber: '#F59E0B',     // Warning, caution
    orange: '#F97316',    // Alert, important notices
    green: '#16A34A',     // Confirmation, completed
    red: '#DC2626',       // Error, destructive actions
    purple: '#7C3AED',    // Secondary brand color
  },

  // Neutral Suite - Grayscale
  neutral: {
    ink: '#0F172A',       // Primary text (default)
    text: '#475569',      // Secondary text
    muted: '#94A3B8',     // Tertiary text, placeholders
    light: '#CBD5E1',     // Light text on dark backgrounds
    border: '#E2E8F0',    // Dividers, borders
    bgLight: '#F8FAFC',   // Very light background
    surface: '#F4F7FF',   // Elevated surface, cards
    white: '#FFFFFF',     // Pure white
  },

  // Semantic colors
  status: {
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
  },

  // Opacity system
  opacity: {
    hover: 0.08,
    focus: 0.12,
    disabled: 0.5,
    overlay: 0.6,
  },
};

export const typography = {
  // Font family
  fontFamily: {
    base: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
    heading: "'Poppins', 'Segoe UI', system-ui, -apple-system, sans-serif",
    mono: "'Menlo', 'Monaco', 'Courier New', monospace",
  },

  // Font sizes
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '28px',
    '4xl': '32px',
  },

  // Font weights
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  /* Weight BY ROLE, rather than by number. Pages that pick weights ad hoc drift
     heavy — every string ends up at 700/800 and the whole screen reads bold,
     because with nothing set in regular there is no light to contrast against.
     Emphasis is relative: it only works if most text is NOT emphasised.
     Reach for these instead of a raw number. */
  weight: {
    body:    500,   // values, descriptions, table cells — the default for prose
    label:   600,   // small uppercase eyebrows, chips, buttons, table headers
    heading: 600,   // section and card headings
    title:   700,   // page title and headline figures — the only heavy tier
  },

  // Line heights
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
    loose: 1.75,
  },

  // Heading styles
  headings: {
    h1: {
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '28px',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: 1.35,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '20px',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.45,
    },
    h6: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.5,
    },
  },

  // Body text styles
  body: {
    large: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    base: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.57,
    },
    small: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.5,
    },
  },

  // Label styles
  label: {
    large: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: 1.43,
      textTransform: 'none',
    },
    base: {
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: 1.33,
    },
    small: {
      fontSize: '11px',
      fontWeight: 600,
      lineHeight: 1.27,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
  },
};

export const spacing = {
  // Base spacing unit = 8px
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',

  // Named spacing
  gutter: '16px',
  section: '24px',
  container: '32px',
};

export const borderRadius = {
  none: '0px',
  sm: '4px',
  base: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  full: '9999px',

  // Component-specific
  button: '6px',
  card: '12px',
  input: '6px',
  tooltip: '6px',
};

export const shadows = {
  none: 'none',
  // Subtle shadow - used for hover states on cards
  subtle: '0 1px 2px rgba(15, 23, 42, 0.03)',
  // Base shadow - default card elevation
  base: '0 4px 12px rgba(15, 23, 42, 0.08)',
  // Hover shadow - elevated on interaction
  hover: '0 10px 24px rgba(15, 23, 42, 0.12)',
  // Focus shadow - keyboard navigation indicator
  focus: '0 12px 32px rgba(15, 23, 42, 0.15)',
  // Dialog shadow - modal elevation
  dialog: '0 20px 40px rgba(15, 23, 42, 0.2)',
  // Popover shadow
  popover: '0 10px 32px rgba(15, 23, 42, 0.12)',
};

export const transitions = {
  // Transition durations
  fast: '150ms',
  base: '200ms',
  slow: '300ms',
  slower: '400ms',

  // Easing functions
  easing: {
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },

  // Common transitions
  default: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  bg: 'background-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  color: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  shadow: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
};

export const breakpoints = {
  xs: '0px',
  sm: '640px',
  md: '1024px',
  lg: '1280px',
  xl: '1536px',
};

export const zIndex = {
  hide: -1,
  base: 0,
  dropdown: 100,
  sticky: 50,
  fixed: 100,
  backdrop: 200,
  modal: 300,
  popover: 250,
  notification: 400,
  tooltip: 500,
};

/**
 * Material-UI Theme Overrides
 */
export const muiTheme = {
  palette: {
    primary: {
      main: colors.primary.blue,
      light: colors.primary.lightBlue,
      dark: colors.primary.navy,
      contrastText: colors.neutral.white,
    },
    secondary: {
      main: colors.accent.teal,
      light: '#16A34A',
      dark: '#059669',
      contrastText: colors.neutral.white,
    },
    success: {
      main: colors.accent.green,
    },
    warning: {
      main: colors.accent.amber,
    },
    error: {
      main: colors.accent.red,
    },
    info: {
      main: colors.primary.blue,
    },
    background: {
      default: colors.neutral.bgLight,
      paper: colors.neutral.surface,
    },
    text: {
      primary: colors.neutral.ink,
      secondary: colors.neutral.text,
      disabled: colors.neutral.muted,
    },
    divider: colors.neutral.border,
  },
  typography: {
    fontFamily: typography.fontFamily.base,
    h1: { ...typography.headings.h1, fontFamily: typography.fontFamily.heading },
    h2: { ...typography.headings.h2, fontFamily: typography.fontFamily.heading },
    h3: { ...typography.headings.h3, fontFamily: typography.fontFamily.heading },
    h4: { ...typography.headings.h4, fontFamily: typography.fontFamily.heading },
    h5: { ...typography.headings.h5, fontFamily: typography.fontFamily.heading },
    h6: { ...typography.headings.h6, fontFamily: typography.fontFamily.heading },
    button: { fontFamily: typography.fontFamily.heading, fontWeight: 500, textTransform: 'none' },
    body1: typography.body.large,
    body2: typography.body.base,
    caption: typography.body.small,
  },
  shape: {
    borderRadius: parseInt(borderRadius.base),
  },
  shadows: [
    shadows.none,
    shadows.subtle,
    shadows.base,
    shadows.base,
    shadows.hover,
    shadows.hover,
    shadows.hover,
    shadows.hover,
    shadows.hover,
    shadows.focus,
    shadows.focus,
    shadows.focus,
    shadows.focus,
    shadows.focus,
    shadows.focus,
    shadows.focus,
    shadows.focus,
    shadows.focus,
    shadows.dialog,
    shadows.dialog,
    shadows.dialog,
    shadows.dialog,
    shadows.dialog,
    shadows.dialog,
    shadows.dialog,
  ],
};

const designTokens = {
  uiScale,
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  breakpoints,
  zIndex,
  muiTheme,
};

export default designTokens;
